import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Briefcase, Bookmark, User, Clock, Loader2, Pencil, Star, Wand2, Download, FileText, CheckCircle, TrendingUp, ExternalLink, Eye, Trash2, Calendar, MapPin, ChevronRight, ChevronLeft, LogOut, Globe, Phone, MoreHorizontal, Users } from "lucide-react";
import { usePWAStandalone } from "../hooks/usePWAStandalone";
import { buildGoogleCalendarUrl, buildInterviewICS, downloadICS, isIOSDevice, openGoogleCalendarUrl } from "../utils/calendarLinks";
import { CalendarPickerSheet } from "../components/CalendarPickerSheet";
import { useAuth, getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useCountry } from "../context/CountryContext";
import { useJobNotifications } from "../context/JobNotificationsContext";
import { useUnreadMessages } from "../context/UnreadMessagesContext";
import { fetchCountries } from "../utils/geoApi";
import { sortCountriesByPriority } from "../utils/countryOrder";
import JobCard from "../components/JobCard";
import CVVersionViewModal from "../components/CVVersionViewModal";
import OfferDetailSheet from "../components/OfferDetailSheet";
import ProfessionalProfileTab from "./ProfessionalProfileTab";
import { CandidateAvatar } from "../components/CandidateAvatar";
import { PrivacySettingsPanel } from "../components/PrivacySettingsPanel";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLAGS = { ES: "🇪🇸", UY: "🇺🇾", US: "🇺🇸", AR: "🇦🇷", BR: "🇧🇷" };
const UI_LANGUAGES = [
  { code: "es", flag: "🇪🇸", name: "Español" }, { code: "en", flag: "🇬🇧", name: "English" }, { code: "pt", flag: "🇧🇷", name: "Português" },
];

// Plain "text + chevron" row — used by the Perfil hub and mirrors the "no
// decorative icons outside Bottom Nav" rule everywhere in this file.
function MenuRow({ label, onClick, to, testid, danger = false }) {
  const className = `w-full flex items-center justify-between gap-3 px-5 py-4 text-left border-b border-slate-100 last:border-0 transition-colors hover:bg-slate-50 ${danger ? "text-red-600" : "text-slate-800"}`;
  const inner = (
    <>
      <span className="text-sm font-semibold font-poppins">{label}</span>
      {!danger && <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />}
    </>
  );
  if (to) return <Link to={to} data-testid={testid} className={className}>{inner}</Link>;
  return <button type="button" onClick={onClick} data-testid={testid} className={className}>{inner}</button>;
}

const STAGE_STYLES = {
  new_applicant: "bg-[#028CF8]/10 text-[#028CF8]",
  contacted:     "bg-amber-50 text-amber-700",
  interview:     "bg-purple-50 text-purple-700",
  offer:         "bg-green-50 text-green-700",
  hired:         "bg-green-100 text-green-800",
  rejected:      "bg-red-50 text-red-700",
};

export default function UserDashboard() {
  const { user, loading: authLoading } = useAuth();
  const { t, language, changeLanguage: changeLanguageGlobal } = useLanguage();
  const { country, setCountry } = useCountry();
  const { subscribeToJobNotifications, markApplicationsRead } = useJobNotifications();
  const { soundEnabled, setSoundEnabled } = useUnreadMessages();
  const isStandalone = usePWAStandalone();

  const STAGE_LABELS = {
    new_applicant: t("stages.new_applicant"),
    contacted: t("stages.contacted"),
    interview: t("stages.interview"),
    offer: t("stages.offer"),
    hired: t("stages.hired"),
    rejected: t("stages.rejected"),
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
    if (diff === 0) return t("jobCard.today");
    if (diff === 1) return t("jobCard.yesterday");
    if (diff < 30) return `${diff}${t("jobCard.dAgo")}`;
    return `${Math.floor(diff / 30)}${t("jobCard.moAgo")}`;
  };
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(searchParams.get("tab") || "applications");
  // Keeps the mobile bottom-nav ↔ dashboard tab in sync via ?tab= so tapping
  // "Recomendados"/"Postulaciones"/"CV"/"Perfil" always lands on the right tab.
  const setTab = (id) => { setTabState(id); setSearchParams({ tab: id }, { replace: true }); };
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== tab) setTabState(urlTab);
  }, [searchParams]);
  // Same tab-switch scroll fix as CompanyDashboard — no route change happens
  // when switching tabs here, so the page must be reset to the top manually.
  useEffect(() => { window.scrollTo(0, 0); }, [tab]);
  // Configuración hub — null shows the 4-row menu (Notificaciones/Privacidad/
  // Idioma/Cuenta), otherwise shows that section's existing content + back row.
  const [settingsSection, setSettingsSection] = useState(null);
  useEffect(() => { if (tab !== "settings") setSettingsSection(null); }, [tab]);
  const [applications, setApplications] = useState([]);
  const [offerDetail, setOfferDetail] = useState(null);
  const [savedJobs, setSavedJobs] = useState([]);
  const [profile, setProfile] = useState({});
  const [userCV, setUserCV] = useState(null);
  const [recommendedJobs, setRecommendedJobs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [ownProfile, setOwnProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [cvVersions, setCvVersions] = useState([]);
  const [viewingVersion, setViewingVersion] = useState(null);
  // "Mis empleos" internal sub-navigation — Guardados | Postulados | Entrevistas
  const [myJobsSubTab, setMyJobsSubTab] = useState("saved");
  // Entrevistas — Próximas | Historial (misma fuente de datos, split por fecha)
  const [interviewsView, setInterviewsView] = useState("upcoming");
  // "Quién me ve" — fetched lazily the first time the tab is opened
  const [profileViews, setProfileViews] = useState(null);
  const [loadingViews, setLoadingViews] = useState(false);
  // "Información de contacto" draft — reuses the same /my/profile endpoint as
  // the Perfil profesional "basic info" editor, just as its own dedicated screen
  const [contactForm, setContactForm] = useState({ name: "", phone: "", location: "", postal_code: "" });
  const [savingContact, setSavingContact] = useState(false);
  const [contactSaved, setContactSaved] = useState(false);
  const [countries, setCountries] = useState([]);
  useEffect(() => { fetchCountries().then(setCountries).catch(() => {}); }, []);
  // Mobile job cards use the compact JobCard variant for a native, less cramped list feel
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
    if (!authLoading && user?.role === "employer") navigate("/company/dashboard");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoading(true);
      try {
        const [appsRes, savedRes, meRes, cvRes, recRes, notifRes, profileRes, versionsRes] = await Promise.all([
          axios.get(`${API}/my/applications`, { headers: getAuthHeaders() }),
          axios.get(`${API}/saved-jobs`, { headers: getAuthHeaders() }),
          axios.get(`${API}/auth/me`, { headers: getAuthHeaders() }),
          axios.get(`${API}/cv`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
          axios.get(`${API}/recommendations?limit=6`, { headers: getAuthHeaders() }).catch(() => ({ data: { jobs: [] } })),
          axios.get(`${API}/notifications`, { headers: getAuthHeaders() }).catch(() => ({ data: { notifications: [], count: 0 } })),
          axios.get(`${API}/my/complete-profile`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
          axios.get(`${API}/cv-versions`, { headers: getAuthHeaders() }).catch(() => ({ data: { versions: [] } })),
        ]);
        setApplications(appsRes.data || []);
        const offerParam = searchParams.get("offer");
        if (offerParam) {
          const matched = (appsRes.data || []).find((a) => a.offer?.offer_id === offerParam)?.offer;
          if (matched) setOfferDetail(matched);
          else axios.get(`${API}/offers/${offerParam}`, { headers: getAuthHeaders() }).then((r) => setOfferDetail(r.data)).catch(() => {});
          const ownerApp = (appsRes.data || []).find((a) => a.offer?.offer_id === offerParam);
          if (ownerApp) markApplicationsRead([ownerApp.application_id]);
        }
        const openAppParam = searchParams.get("open_application");
        if (openAppParam) {
          markApplicationsRead([openAppParam]);
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="application-${openAppParam}"]`);
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
              el.style.boxShadow = "0 0 0 3px rgba(2,140,248,0.4)";
              setTimeout(() => { el.style.boxShadow = ""; }, 2500);
            }
          }, 400);
        }
        setSavedJobs(savedRes.data || []);
        setProfile(meRes.data || {});
        setProfileForm(meRes.data || {});
        setUserCV(cvRes.data && cvRes.data.user_id ? cvRes.data : null);
        setRecommendedJobs(recRes.data?.jobs || []);
        setNotifications(notifRes.data?.notifications || []);
        setNotifCount(notifRes.data?.count || 0);
        setOwnProfile(profileRes.data);
        setCvVersions(versionsRes.data?.versions || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
    // Depend on identity (user_id), not the whole `user` object — updateUser() patches
    // (e.g. a new avatar) create a new object reference but must NOT re-trigger this
    // full dashboard reload/spinner, which was unmounting the Profile tab mid-toast.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // Realtime: entrevista programada/actualizada u oferta retirada por el
  // empleador mientras el candidato tiene la app abierta — parchea el mismo
  // estado que ya renderiza la UI (application.offer / interview_details),
  // sin refetch manual ni segunda fuente de datos.
  useEffect(() => {
    const unsubscribe = subscribeToJobNotifications((notification) => {
      if (!notification) return;
      const { type, related_application_id, metadata } = notification;
      if (!related_application_id) return;
      if (type === "offer_withdrawn") {
        setApplications((prev) => prev.map((a) => (
          a.application_id === related_application_id && a.offer
            ? { ...a, offer: { ...a.offer, status: "withdrawn" } }
            : a
        )));
        setOfferDetail((prev) => (prev && prev.offer_id === metadata?.offer_id ? { ...prev, status: "withdrawn" } : prev));
      } else if (type === "interview_scheduled" || type === "interview_updated") {
        setApplications((prev) => prev.map((a) => (
          a.application_id === related_application_id
            ? { ...a, stage: "interview", interview_details: metadata?.interview || a.interview_details }
            : a
        )));
      }
    });
    return unsubscribe;
  }, [subscribeToJobNotifications]);

  // Entrar a "Mis empleos" (Postulados/Entrevistas) y ver la lista ES
  // visualizar esos avisos — nunca se marca como leído solo por abrir la app.
  useEffect(() => {
    const viewingApplications = tab === "applications" || (tab === "myjobs" && (myJobsSubTab === "applications" || myJobsSubTab === "interviews"));
    if (!viewingApplications || applications.length === 0) return;
    markApplicationsRead(applications.map((a) => a.application_id));
  }, [tab, myJobsSubTab, applications.length, markApplicationsRead]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/my/profile`, {
        name: profileForm.name,
        phone: profileForm.phone,
        location: profileForm.location,
        linkedin: profileForm.linkedin,
        bio: profileForm.bio,
      }, { headers: getAuthHeaders() });
      setProfile(res.data);
      setEditingProfile(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Used by the Professional Profile tab after every section save (summary, experience,
  // education, skills, languages, preferences) — refreshes the same /cv + /my/complete-profile
  // sources of truth so the CV Builder and this tab never drift apart.
  const refetchProfile = async () => {
    try {
      const [meRes, cvRes, profileRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers: getAuthHeaders() }),
        axios.get(`${API}/cv`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
        axios.get(`${API}/my/complete-profile`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
      ]);
      setProfile(meRes.data || {});
      setUserCV(cvRes.data && cvRes.data.user_id ? cvRes.data : null);
      setOwnProfile(profileRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const removeSavedJob = async (jobId) => {
    await axios.delete(`${API}/saved-jobs/${jobId}`, { headers: getAuthHeaders() });
    setSavedJobs((prev) => prev.filter((j) => j.job_id !== jobId));
  };

  // JobCard already performs the DELETE itself via its own bookmark button —
  // this only syncs the local "Saved Jobs" list so the card disappears once unsaved.
  const handleSavedJobToggle = (jobId, nowSaved) => {
    if (!nowSaved) setSavedJobs((prev) => prev.filter((j) => j.job_id !== jobId));
  };

  // "Quién me ve" — lazy-loaded the first time the candidate opens the tab
  const loadProfileViews = async () => {
    if (profileViews !== null || loadingViews) return;
    setLoadingViews(true);
    try {
      const res = await axios.get(`${API}/my/profile-views`, { headers: getAuthHeaders() });
      setProfileViews(res.data?.views || []);
    } catch { setProfileViews([]); }
    finally { setLoadingViews(false); }
  };

  // "Información de contacto" — same /my/profile source of truth as the
  // Perfil profesional "basic info" editor, just its own dedicated screen
  useEffect(() => {
    if (tab === "settings" && profile) {
      setContactForm({ name: profile.name || "", phone: profile.phone || "", location: profile.location || "", postal_code: profile.postal_code || "" });
    }
    if (tab === "whoviewedme") loadProfileViews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, profile]);

  const saveContact = async () => {
    setSavingContact(true);
    setContactSaved(false);
    try {
      const res = await axios.put(`${API}/my/profile`, contactForm, { headers: getAuthHeaders() });
      setProfile(res.data);
      setContactSaved(true);
      setTimeout(() => setContactSaved(false), 3000);
    } catch (err) { console.error(err); }
    finally { setSavingContact(false); }
  };

  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const toggleFindable = async () => {
    const next = !profile.open_to_work;
    setProfile((p) => ({ ...p, open_to_work: next })); // instant, optimistic
    setVisibilitySaving(true);
    try {
      const res = await axios.put(`${API}/my/profile`, { open_to_work: next }, { headers: getAuthHeaders() });
      setProfile(res.data);
    } catch (err) {
      console.error(err);
      setProfile((p) => ({ ...p, open_to_work: !next })); // never show a state the backend didn't confirm
      toast.error(t("profileHub.visibilitySaveError"));
    } finally {
      setVisibilitySaving(false);
    }
  };

  // Selector "Agregar al calendario" (Google / Apple) — ver utils/calendarLinks.js
  const [calendarSheetApp, setCalendarSheetApp] = useState(null);
  const handleCalendarSelect = (providerId) => {
    const app = calendarSheetApp;
    setCalendarSheetApp(null);
    if (!app) return;
    if (providerId === "google") {
      const url = buildGoogleCalendarUrl(app, t);
      if (url) openGoogleCalendarUrl(url, isStandalone && isIOSDevice());
    } else if (providerId === "apple") {
      const ics = buildInterviewICS(app, t);
      if (ics) downloadICS(ics, "entrevista.ics");
    }
  };


  const downloadPrimaryCv = async (format) => {
    const mime = format === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    const res = await axios.get(`${API}/cv/download-${format}`, { headers: getAuthHeaders(), responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${(userCV?.name || "CV").replace(/\s+/g, "_")}.${format}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadVersionPdf = async (versionId, versionName) => {
    const res = await axios.get(`${API}/cv-versions/${versionId}/download-pdf`, { headers: getAuthHeaders(), responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${(versionName || "CV").replace(/\s+/g, "_")}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const deleteVersion = async (versionId) => {
    if (!window.confirm(t("cvVersions.deleteConfirm"))) return;
    await axios.delete(`${API}/cv-versions/${versionId}`, { headers: getAuthHeaders() });
    setCvVersions((prev) => prev.filter((v) => v.version_id !== versionId));
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#028CF8] animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: "recommended",  label: t("tabs.recommended"),          icon: Star,     count: recommendedJobs.length },
    { id: "applications", label: t("tabs.applications"),          icon: Briefcase,count: applications.length },
    { id: "saved",        label: t("tabs.savedJobs"),             icon: Bookmark, count: savedJobs.length },
    { id: "cv",           label: t("tabs.myCV"),                  icon: FileText, count: null },
    { id: "myprofile",    label: t("tabs.professionalProfile"),   icon: User,     count: null },
  ];
  const interviews = applications.filter((a) => a.stage === "interview" && a.interview_details && Object.keys(a.interview_details).length > 0);
  const todayStr = new Date().toISOString().slice(0, 10);
  const upcomingInterviews = interviews.filter((a) => (a.interview_details.date || "") >= todayStr)
    .sort((a, b) => `${a.interview_details.date}${a.interview_details.time || ""}`.localeCompare(`${b.interview_details.date}${b.interview_details.time || ""}`));
  const pastInterviews = interviews.filter((a) => (a.interview_details.date || "") < todayStr)
    .sort((a, b) => `${b.interview_details.date}${b.interview_details.time || ""}`.localeCompare(`${a.interview_details.date}${a.interview_details.time || ""}`));
  const MYJOBS_SUBTABS = [
    { id: "saved",        label: t("myJobs.tabSaved"),        count: savedJobs.length },
    { id: "applications", label: t("myJobs.tabApplications"), count: applications.length },
    { id: "interviews",   label: t("myJobs.tabInterviews"),   count: interviews.length },
  ];

  return (
    <div data-testid="user-dashboard" className="dashboard-page-shell bg-slate-50">
      {/* Notification banner — new high-match jobs. Single logical place: only
          on the "Postulados" view (desktop "Postulaciones" tab / mobile "Mis
          empleos → Postulados" sub-tab) — NOT on every /dashboard tab. */}
      {notifCount > 0 && (tab === "applications" || (tab === "myjobs" && myJobsSubTab === "applications")) && (
        <div
          style={{ background: "linear-gradient(90deg, rgba(2,140,248,0.06), rgba(245,95,165,0.04))", borderBottom: "1px solid rgba(2,140,248,0.15)", padding: "12px 0", cursor: "pointer" }}
          onClick={() => setShowNotifPanel(!showNotifPanel)}
        >
          <div className="container mx-auto px-4 md:px-6 lg:px-8 flex items-center gap-3">
            <div style={{ width: 28, height: 28, borderRadius: "8px", background: "#028CF8", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Star style={{ width: 14, height: 14, color: "white" }} />
            </div>
            <p style={{ fontSize: "13px", fontWeight: 600, color: "#0F172A", fontFamily: "Poppins, sans-serif", flex: 1 }}>
              <span style={{ color: "#028CF8" }}>{notifCount} {notifCount > 1 ? t("userDash.newJobPlural") : t("userDash.newJobSingular")}</span> {t("userDash.matchProfileDesc")}
            </p>
            <span style={{ fontSize: "11px", color: "#028CF8", fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>
              {showNotifPanel ? `${t("common.close")} ▲` : `${t("common.seeAll")} ▼`}
            </span>
          </div>
          {showNotifPanel && notifications.length > 0 && (
            <div className="container mx-auto px-4 md:px-6 lg:px-8 mt-3">
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {notifications.map((n) => (
                  <Link key={n.job_id} to={`/jobs/${n.job_id}`}
                    style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "6px 12px", background: "white", border: "1px solid #E5E7EB", borderRadius: "999px", fontSize: "12px", fontWeight: 600, color: "#0F172A", fontFamily: "Poppins, sans-serif", textDecoration: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                    <span style={{ fontSize: "10px", fontWeight: 700, padding: "1px 6px", borderRadius: "999px", background: "rgba(16,185,129,0.1)", color: "#059669" }}>{n.match_score}%</span>
                    {n.title} · {n.company}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Header — desktop only; mobile uses the global compact header instead */}
      <div className="hidden lg:block bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <CandidateAvatar name={user?.name} picture={ownProfile?.picture || user?.picture} size={48} rounded="xl" />
            <div>
              <h1 className="font-poppins text-xl font-bold text-slate-900">{t("userDash.welcome")}, {user?.name?.split(" ")[0]}!</h1>
              <p className="text-slate-500 text-sm">{user?.email}</p>
            </div>
            <div className="ml-auto hidden sm:flex items-center gap-3">
              <div className="text-center px-4">
                <p className="font-poppins text-xl font-bold text-slate-900">{applications.length}</p>
                <p className="text-xs text-slate-500">{t("userDash.applications")}</p>
              </div>
              {notifCount > 0 && (
                <div className="text-center px-4">
                  <div style={{ position: "relative", display: "inline-block" }}>
                    <p className="font-poppins text-xl font-bold" style={{ color: "#028CF8" }}>{notifCount}</p>
                    <p className="text-xs text-slate-500">{t("userDash.newMatches")}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6">
        {/* Tabs — desktop only; mobile navigation is covered by Bottom Nav + hamburger drawer */}
        <div className="hidden lg:flex gap-1 bg-white rounded-xl border border-slate-200 p-1 mb-6" data-testid="dashboard-tabs">
          {tabs.map(({ id, label, icon: Icon, count }) => (
            <button
              key={id}
              data-testid={`tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${tab === id ? "bg-[#028CF8] text-white shadow" : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"}`}
            >
              <Icon className="h-4 w-4" />
              {label}
              {count !== null && count > 0 && (
                <span className={`text-xs rounded-full px-1.5 py-0.5 ${tab === id ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"}`}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Recommended Jobs Tab ── */}
        {tab === "recommended" && (
          <div data-testid="recommended-tab">
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-xs font-semibold text-[#028CF8] uppercase tracking-widest mb-1 font-poppins">{t("cv.aiPowered")}</p>
                <h2 className="font-poppins text-xl font-bold text-slate-900">{t("userDash.recommendedTitle")}</h2>
              </div>
              {!userCV && (
                <Link to="/cv-builder" className="flex items-center gap-2 text-sm font-semibold text-[#028CF8] border border-[#028CF8]/30 rounded-xl px-4 py-2 hover:bg-[#028CF8]/5 transition-all font-poppins">
                  <Wand2 className="h-4 w-4" /> {t("userDash.createCvBetterMatches")}
                </Link>
              )}
            </div>
            {!userCV && (
              <div style={{ background: "linear-gradient(135deg, #EFF6FF, #F0F4FF)", border: "1px solid #BFDBFE", borderRadius: "16px", padding: "24px", marginBottom: "24px", display: "flex", alignItems: "center", gap: "16px" }}>
                <div style={{ width: 48, height: 48, borderRadius: "12px", background: "#028CF8", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <TrendingUp style={{ width: 22, height: 22, color: "white" }} />
                </div>
                <div>
                  <p style={{ fontSize: "14px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif", marginBottom: "4px" }}>{t("userDash.unlockTitle")}</p>
                  <p style={{ fontSize: "13px", color: "#64748B", fontFamily: "Poppins, sans-serif" }}>{t("userDash.unlockDesc")}</p>
                </div>
                <Link to="/cv-builder" style={{ flexShrink: 0, height: "38px", padding: "0 18px", background: "#028CF8", color: "white", borderRadius: "10px", fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif", display: "flex", alignItems: "center", gap: "6px", textDecoration: "none" }}>
                  <Wand2 style={{ width: 14, height: 14 }} /> {t("userDash.getStarted")}
                </Link>
              </div>
            )}
            {recommendedJobs.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#E5E7EB] p-12 text-center shadow-sm">
                <Star className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-poppins text-lg font-semibold text-slate-700 mb-2">{t("userDash.noRecsTitle")}</h3>
                <p className="text-slate-500 text-sm">{t("userDash.noRecsDesc")}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {recommendedJobs.map((job) => <JobCard key={job.job_id} job={job} compact showMatchScore />)}
              </div>
            )}
          </div>
        )}

        {/* ── CV Tab ── */}
        {tab === "cv" && (
          <div data-testid="cv-tab">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-poppins text-xl font-bold text-slate-900">{t("tabs.myCV")}</h2>
              </div>
              <Link to="/cv-builder" className="flex items-center gap-2 text-sm font-semibold text-white bg-[#028CF8] rounded-xl px-4 py-2 hover:bg-[#0270C5] transition-all font-poppins">
                <Wand2 className="h-4 w-4" /> {userCV ? t("userDash.editCV") : t("userDash.createCV")}
              </Link>
            </div>

            {!userCV ? (
              <div className="bg-white rounded-2xl border border-[#E5E7EB] p-12 text-center shadow-sm">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-poppins text-lg font-semibold text-slate-700 mb-2">{t("userDash.noCvYetTitle")}</h3>
                <p className="text-slate-500 text-sm mb-5">{t("userDash.noCvYetDesc")}</p>
                <Link to="/cv-builder" className="btn-primary text-sm rounded-xl">
                  <Wand2 className="h-4 w-4" /> {t("userDash.createCvWithAI")}
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {/* CV Score Card */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6" style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "16px", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                  <div className="flex items-center gap-4 sm:contents">
                  {/* Score ring */}
                  <div style={{ textAlign: "center", flexShrink: 0 }}>
                    {(() => {
                      const score = userCV.cv_score || 0;
                      const color = score >= 80 ? "#10B981" : score >= 60 ? "#028CF8" : "#64748B";
                      return (
                        <div style={{ width: 72, height: 72, borderRadius: "50%", border: `6px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", background: `${color}10`, marginBottom: "6px" }}>
                          <span style={{ fontSize: "20px", fontWeight: 700, color, fontFamily: "Poppins, sans-serif" }}>{score}</span>
                        </div>
                      );
                    })()}
                    <span style={{ fontSize: "11px", color: "#64748B", fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>{t("cv.cvScore")}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p style={{ fontSize: "16px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif", marginBottom: "4px" }}>{userCV.name}</p>
                    {userCV.title && <p style={{ fontSize: "13px", color: "#028CF8", fontWeight: 600, fontFamily: "Poppins, sans-serif", marginBottom: "8px" }}>{userCV.title}</p>}
                    {userCV.suggestions?.length > 0 && (
                      <div>
                        <p style={{ fontSize: "11px", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "4px", fontFamily: "Poppins, sans-serif" }}>{t("cv.suggestions")}</p>
                        {userCV.suggestions.slice(0, 3).map((s, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#64748B", fontFamily: "Poppins, sans-serif", marginBottom: "2px" }}>
                            <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#64748B", flexShrink: 0 }} /> {t(`cvSuggestions.${s}`)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  </div>
                  <div className="flex flex-row sm:flex-col gap-2 w-full sm:w-auto flex-shrink-0">
                    <button data-testid="download-primary-cv-pdf" onClick={() => downloadPrimaryCv("pdf")}
                      className="flex-1 sm:flex-initial"
                      style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", height: "36px", padding: "0 14px", background: "#028CF8", color: "white", borderRadius: "9px", fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif", border: "none", cursor: "pointer" }}>
                      <Download style={{ width: 13, height: 13 }} /> PDF
                    </button>
                    <button data-testid="download-primary-cv-docx" onClick={() => downloadPrimaryCv("docx")}
                      className="flex-1 sm:flex-initial"
                      style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", height: "36px", padding: "0 14px", background: "white", color: "#64748B", border: "1px solid #E5E7EB", borderRadius: "9px", fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif", cursor: "pointer" }}>
                      <Download style={{ width: 13, height: 13 }} /> Word
                    </button>
                  </div>
                </div>

                {/* CV Summary */}
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 shadow-sm">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 text-sm">
                    <div><p className="text-xs text-slate-600 uppercase tracking-wide mb-1 font-poppins">{t("cv.stepExperience")}</p><p className="font-semibold text-slate-900 font-poppins">{userCV.experience?.length || 0} {t("userDash.positionsSuffix")}</p></div>
                    <div><p className="text-xs text-slate-600 uppercase tracking-wide mb-1 font-poppins">{t("profProfile.skills")}</p><p className="font-semibold text-slate-900 font-poppins">{userCV.skills?.length || 0} {t("userDash.listedSuffix")}</p></div>
                    <div><p className="text-xs text-slate-600 uppercase tracking-wide mb-1 font-poppins">{t("userDash.lastUpdated")}</p><p className="font-semibold text-slate-900 font-poppins">{userCV.last_updated ? new Date(userCV.last_updated).toLocaleDateString() : "—"}</p></div>
                  </div>
                  {userCV.skills?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-xs text-slate-600 uppercase tracking-wide mb-2 font-poppins">{t("userDash.topSkills")}</p>
                      <div className="flex flex-wrap gap-2">
                        {userCV.skills.slice(0, 8).map((s) => (
                          <span key={s} style={{ fontSize: "11px", fontWeight: 600, padding: "3px 10px", borderRadius: "999px", background: "#EFF6FF", color: "#028CF8", fontFamily: "Poppins, sans-serif" }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* CV Versions adapted for specific job offers */}
            {userCV && (
              <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 shadow-sm mt-4" data-testid="cv-versions-section">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-poppins text-base font-bold text-slate-900">{t("cvVersions.title")}</h3>
                  {cvVersions.length > 0 && <span className="text-xs text-slate-400">{cvVersions.length}</span>}
                </div>
                {cvVersions.length === 0 ? (
                  <p className="text-sm text-slate-500" data-testid="cv-versions-empty">{t("cvVersions.empty")}</p>
                ) : (
                  <div className="space-y-3">
                    {cvVersions.map((v) => (
                      <div key={v.version_id} data-testid={`cv-version-${v.version_id}`}
                        className="flex items-center justify-between gap-3 border border-slate-100 rounded-xl p-3.5 hover:border-[#028CF8]/30 transition-colors">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="text-sm font-semibold text-slate-900 truncate">{v.version_name}</p>
                            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-[#F55FA5]/10 text-[#F55FA5] flex-shrink-0">{t("cvVersions.adaptedBadge")}</span>
                          </div>
                          <p className="text-xs text-slate-500 truncate">{v.target_job_title} · {v.target_company_name}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{v.created_at ? new Date(v.created_at).toLocaleDateString() : "—"}</p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={() => setViewingVersion(v)} data-testid={`cv-version-view-${v.version_id}`} title={t("cvVersions.view")}
                            className="p-2 rounded-lg text-slate-400 hover:text-[#028CF8] hover:bg-[#028CF8]/5 transition-colors">
                            <Eye className="h-4 w-4" />
                          </button>
                          <button onClick={() => downloadVersionPdf(v.version_id, v.version_name)} data-testid={`cv-version-download-${v.version_id}`} title={t("cvVersions.downloadPdf")}
                            className="p-2 rounded-lg text-slate-400 hover:text-[#028CF8] hover:bg-[#028CF8]/5 transition-colors">
                            <Download className="h-4 w-4" />
                          </button>
                          <button onClick={() => deleteVersion(v.version_id)} data-testid={`cv-version-delete-${v.version_id}`} title={t("cvVersions.delete")}
                            className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {tab === "applications" && (
          <div data-testid="applications-tab">
            <h2 className="font-poppins text-lg font-bold text-slate-900 mb-4">{t("userDash.myApplications")}</h2>
            {applications.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#E5E7EB] p-12 text-center shadow-sm">
                <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-space-grotesk text-lg font-semibold text-slate-700 mb-2">{t("userDash.noApplications")}</h3>
                <p className="text-slate-500 text-sm mb-4">{t("userDash.noApplicationsDesc")}</p>
                <Link to="/jobs" className="btn-primary text-sm">{t("userDash.browseJobs")}</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {applications.map((app) => (
                  <div key={app.application_id} data-testid={`application-${app.application_id}`} className="bg-white rounded-2xl border border-[#E5E7EB] p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-[#028CF8]/30 transition-all duration-200">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-[#028CF8]/10 flex items-center justify-center font-bold text-[#028CF8] font-space-grotesk overflow-hidden">
                          {app.company_logo ? <img src={app.company_logo} alt={app.company_name} className="h-full w-full object-cover" /> : app.company_name?.[0]}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900 text-sm">{app.job_title}</h3>
                          <p className="text-xs text-slate-500">{app.company_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STAGE_STYLES[app.stage] || "bg-slate-100 text-slate-600"}`}>
                          {STAGE_LABELS[app.stage] || app.stage}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
                      <Clock className="h-3 w-3" /> {t("userDash.applied")} {timeAgo(app.applied_date)}
                    </div>
                    {app.offer && (
                      <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-100" data-testid={`offer-banner-${app.application_id}`}>
                        <p className="text-xs font-semibold text-slate-700">
                          {t(`userDash.offerBanner_${app.offer.status}`)}
                        </p>
                        <button onClick={() => setOfferDetail(app.offer)} data-testid={`view-offer-${app.application_id}`}
                          className="text-xs font-bold text-[#028CF8] hover:underline whitespace-nowrap flex-shrink-0">
                          {t("userDash.viewOffer")}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Saved Jobs Tab */}
        {tab === "saved" && (
          <div data-testid="saved-jobs-tab">
            <h2 className="font-space-grotesk text-lg font-bold text-slate-900 mb-4">{t("userDash.savedJobs")}</h2>
            {savedJobs.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#E5E7EB] p-12 text-center shadow-sm">
                <Bookmark className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-space-grotesk text-lg font-semibold text-slate-700 mb-2">{t("userDash.noSavedJobs")}</h3>
                <p className="text-slate-500 text-sm mb-4">{t("userDash.noSavedJobsDesc")}</p>
                <Link to="/jobs" className="btn-primary text-sm">{t("userDash.findJobs")}</Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {savedJobs.map((job) => (
                  <JobCard key={job.job_id} job={{ ...job, is_saved: true }} compact={isMobile} onSaveToggle={handleSavedJobToggle} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Professional Profile Tab */}
        {tab === "myprofile" && (
          <div data-testid="myprofile-tab">
            <div className="mb-5">
              <h2 className="font-poppins text-xl font-bold text-slate-900">{t("profile.myProfile")}</h2>
            </div>

            {ownProfile ? (
              <ProfessionalProfileTab t={t} user={user} ownProfile={ownProfile} onRefetch={refetchProfile} />
            ) : (
              <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#028CF8]" /></div>
            )}
          </div>
        )}

        {/* ── Mis Empleos (mobile primary entry) — Guardados | Postulados | Entrevistas ── */}
        {tab === "myjobs" && (
          <div data-testid="myjobs-tab">
            <h2 className="font-poppins text-lg font-bold text-slate-900 mb-4">{t("myJobs.title")}</h2>
            <div className="grid grid-cols-3 gap-1 bg-white rounded-xl border border-slate-200 p-1 mb-5 w-full" data-testid="myjobs-subtabs">
              {MYJOBS_SUBTABS.map(({ id, label, count }) => (
                <button key={id} data-testid={`myjobs-subtab-${id}`} onClick={() => setMyJobsSubTab(id)}
                  className={`min-w-0 flex items-center justify-center gap-1 px-1 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all truncate ${myJobsSubTab === id ? "bg-[#028CF8] text-white shadow" : "text-slate-500 hover:bg-slate-50"}`}>
                  <span className="truncate">{label}</span>
                  {count > 0 && <span className={`flex-shrink-0 text-[10px] sm:text-xs rounded-full px-1.5 py-0.5 ${myJobsSubTab === id ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"}`}>{count}</span>}
                </button>
              ))}
            </div>

            {myJobsSubTab === "saved" && (
              <div data-testid="myjobs-saved-panel">
                {savedJobs.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-[#E5E7EB] p-12 text-center shadow-sm" data-testid="myjobs-saved-empty">
                    <Bookmark className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-poppins text-lg font-semibold text-slate-700 mb-2">{t("myJobs.savedEmptyTitle")}</h3>
                    <p className="text-slate-500 text-sm mb-4">{t("myJobs.savedEmptyDesc")}</p>
                    <Link to="/jobs" className="btn-primary text-sm">{t("myJobs.savedCta")}</Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {savedJobs.map((job) => (
                      <JobCard key={job.job_id} job={{ ...job, is_saved: true }} compact={isMobile} onSaveToggle={handleSavedJobToggle} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {myJobsSubTab === "applications" && (
              <div data-testid="myjobs-applications-panel">
                {applications.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-[#E5E7EB] p-12 text-center shadow-sm" data-testid="myjobs-applications-empty">
                    <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-poppins text-lg font-semibold text-slate-700 mb-2">{t("myJobs.appsEmptyTitle")}</h3>
                    <p className="text-slate-500 text-sm mb-4">{t("myJobs.appsEmptyDesc")}</p>
                    <Link to="/jobs" className="btn-primary text-sm">{t("myJobs.savedCta")}</Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {applications.map((app) => (
                      <Link key={app.application_id} to={`/jobs/${app.job_id}`} data-testid={`myjobs-application-${app.application_id}`}
                        className="block bg-white rounded-2xl border border-[#E5E7EB] p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-[#028CF8]/30 transition-all duration-200">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-[#028CF8]/10 flex items-center justify-center font-bold text-[#028CF8] font-space-grotesk overflow-hidden">{app.company_logo ? <img src={app.company_logo} alt={app.company_name} className="h-full w-full object-cover" /> : app.company_name?.[0]}</div>
                            <div>
                              <h3 className="font-semibold text-slate-900 text-sm">{app.job_title}</h3>
                              <p className="text-xs text-slate-500">{app.company_name}</p>
                            </div>
                          </div>
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STAGE_STYLES[app.stage] || "bg-slate-100 text-slate-600"}`}>{STAGE_LABELS[app.stage] || app.stage}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
                          <Clock className="h-3 w-3" /> {t("userDash.applied")} {timeAgo(app.applied_date)}
                        </div>
                        {app.offer && (
                          <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-100" data-testid={`offer-banner-mobile-${app.application_id}`}>
                            <p className="text-xs font-semibold text-slate-700">{t(`userDash.offerBanner_${app.offer.status}`)}</p>
                            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOfferDetail(app.offer); }} data-testid={`view-offer-mobile-${app.application_id}`}
                              className="text-xs font-bold text-[#028CF8] hover:underline whitespace-nowrap flex-shrink-0">
                              {t("userDash.viewOffer")}
                            </button>
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {myJobsSubTab === "interviews" && (
              <div data-testid="myjobs-interviews-panel">
                <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-4 w-fit" data-testid="myjobs-interviews-view-toggle">
                  <button onClick={() => setInterviewsView("upcoming")} data-testid="myjobs-interviews-view-upcoming"
                    className={`px-3.5 min-h-[40px] rounded-md text-xs font-semibold transition-all ${interviewsView === "upcoming" ? "bg-white text-[#028CF8] shadow-sm" : "text-slate-500"}`}>
                    {t("myJobs.interviewsUpcomingTab")} {upcomingInterviews.length > 0 && `(${upcomingInterviews.length})`}
                  </button>
                  <button onClick={() => setInterviewsView("history")} data-testid="myjobs-interviews-view-history"
                    className={`px-3.5 min-h-[40px] rounded-md text-xs font-semibold transition-all ${interviewsView === "history" ? "bg-white text-[#028CF8] shadow-sm" : "text-slate-500"}`}>
                    {t("myJobs.interviewsHistoryTab")} {pastInterviews.length > 0 && `(${pastInterviews.length})`}
                  </button>
                </div>
                {(interviewsView === "upcoming" ? upcomingInterviews : pastInterviews).length === 0 ? (
                  <div className="bg-white rounded-2xl border border-[#E5E7EB] p-12 text-center shadow-sm" data-testid="myjobs-interviews-empty">
                    <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-poppins text-lg font-semibold text-slate-700 mb-2">
                      {interviewsView === "upcoming" ? t("myJobs.interviewsEmptyTitle") : t("myJobs.interviewsHistoryEmptyTitle")}
                    </h3>
                    <p className="text-slate-500 text-sm">
                      {interviewsView === "upcoming" ? t("myJobs.interviewsEmptyDesc") : t("myJobs.interviewsHistoryEmptyDesc")}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(interviewsView === "upcoming" ? upcomingInterviews : pastInterviews).map((app) => {
                      const iv = app.interview_details || {};
                      return (
                        <div key={app.application_id} data-testid={`myjobs-interview-${app.application_id}`}
                          className="bg-white rounded-2xl border border-[#E5E7EB] p-5 shadow-sm">
                          <p className="text-xs font-bold text-[#028CF8] uppercase tracking-wide mb-1">{t("myJobs.interviewLabel")}</p>
                          <h3 className="font-poppins font-bold text-slate-900 text-base mb-0.5">{app.job_title}</h3>
                          <p className="text-sm text-slate-500 mb-3">{app.company_name}</p>
                          <div className="space-y-1.5 text-sm text-slate-700">
                            {iv.date && (
                              <p className="flex items-center gap-2">
                                <Calendar className="h-3.5 w-3.5 text-slate-400" /> {iv.date}{iv.time ? ` · ${iv.time}` : ""}{iv.end_time ? `–${iv.end_time}` : ""}
                              </p>
                            )}
                            {iv.modality === "in_person" && iv.location && <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-slate-400" /> {iv.location}</p>}
                            {iv.modality === "video" && iv.link && <p className="flex items-center gap-2"><ExternalLink className="h-3.5 w-3.5 text-slate-400" /> <a href={iv.link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#028CF8] font-semibold truncate">{iv.link}</a></p>}
                            {iv.modality === "phone" && <p className="flex items-center gap-2 text-slate-500"><Phone className="h-3.5 w-3.5 text-slate-400" /> {t("notifyApplicant.modality_phone")}</p>}
                            {iv.modality === "other" && <p className="flex items-center gap-2 text-slate-500"><MoreHorizontal className="h-3.5 w-3.5 text-slate-400" /> {t("notifyApplicant.modality_other")}</p>}
                            {iv.contact_name && <p className="flex items-center gap-2 text-slate-500"><Users className="h-3.5 w-3.5 text-slate-400" /> {t("myJobs.withLabel")}: {iv.contact_name}{iv.contact_role ? ` — ${iv.contact_role}` : ""}</p>}
                            {iv.notes && <p className="text-slate-500">{iv.notes}</p>}
                          </div>
                          {iv.date && interviewsView === "upcoming" && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setCalendarSheetApp(app); }}
                              data-testid={`myjobs-interview-calendar-${app.application_id}`}
                              className="w-full mt-4 h-[54px] rounded-2xl border-[1.5px] border-[#028CF8] bg-[#028CF8]/[0.03] text-[#028CF8] text-sm font-bold flex items-center justify-center gap-2.5 hover:bg-[#028CF8]/[0.07] transition-colors"
                            >
                              <Calendar className="h-4.5 w-4.5" /> {t("myJobs.addToCalendar")}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Quién me ve ── */}
        {tab === "whoviewedme" && (
          <div data-testid="whoviewedme-tab">
            <h2 className="font-poppins text-lg font-bold text-slate-900 mb-4">{t("whoViewedMe.title")}</h2>
            {loadingViews ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#028CF8]" /></div>
            ) : !profileViews || profileViews.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#E5E7EB] p-12 text-center shadow-sm" data-testid="whoviewedme-empty">
                <Eye className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-poppins text-lg font-semibold text-slate-700 mb-2">{t("whoViewedMe.emptyTitle")}</h3>
                <p className="text-slate-500 text-sm mb-4">{t("whoViewedMe.emptyDesc")}</p>
                <button onClick={() => setTab("myprofile")} data-testid="whoviewedme-cta" className="btn-primary text-sm">{t("whoViewedMe.cta")}</button>
              </div>
            ) : (
              <div className="space-y-3">
                {profileViews.map((v) => (
                  <div key={v.view_id} data-testid={`whoviewedme-item-${v.view_id}`} className="bg-white rounded-2xl border border-[#E5E7EB] p-4 shadow-sm flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-[#028CF8]/10 flex items-center justify-center font-bold text-[#028CF8] flex-shrink-0 overflow-hidden">
                      {v.company_logo ? <img src={v.company_logo} alt={v.company_name} className="h-full w-full object-cover" /> : (v.company_name?.[0] || "?")}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-slate-900 truncate">{v.company_name}</p>
                      <p className="text-xs text-slate-500">{timeAgo(v.viewed_at)}</p>
                      {v.related_job_title && <p className="text-xs text-[#028CF8] font-semibold mt-0.5">{t("whoViewedMe.relatedTo")}: {v.related_job_title}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Perfil hub (mobile primary entry) ── */}
        {tab === "profile" && (
          <div data-testid="profile-hub-tab" className="max-w-lg">
            <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 shadow-sm mb-4 flex items-center gap-4">
              <CandidateAvatar name={user?.name} picture={profile?.picture || user?.picture} size={56} rounded="xl" />
              <div className="min-w-0 flex-1">
                <p className="font-poppins font-bold text-slate-900 truncate">{user?.name}</p>
                {profile?.location && <p className="text-xs text-slate-500 truncate">{profile.location}</p>}
              </div>
            </div>

            {/* Control de visibilidad — reusa `open_to_work` (persistido en backend y
                respetado por TODOS los endpoints que exponen candidatos a empresas:
                Talent Pool, GET /profile/{id}), con copy explícito de qué significa
                cada estado (no es solo un toggle visual). */}
            <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm p-4 mb-4" data-testid="profile-visibility-card">
              <div className="flex items-center justify-between gap-3">
                <span className={`text-sm font-poppins font-bold ${profile?.open_to_work ? "text-[#028CF8]" : "text-slate-700"}`}>
                  {profile?.open_to_work ? t("profileHub.visibilityOnTitle") : t("profileHub.visibilityOffTitle")}
                </span>
                <Switch checked={!!profile?.open_to_work} onCheckedChange={toggleFindable} disabled={visibilitySaving}
                  data-testid="profile-hub-findable-toggle" className="data-[state=checked]:bg-[#028CF8]" />
              </div>
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                {profile?.open_to_work ? t("profileHub.visibilityOnDesc") : t("profileHub.visibilityOffDesc")}
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm overflow-hidden">
              <MenuRow label={t("profileHub.rowProfessionalProfile")} onClick={() => setTab("myprofile")} testid="profile-hub-row-professional" />
              <MenuRow label={t("profileHub.rowCV")} onClick={() => setTab("cv")} testid="profile-hub-row-cv" />
            </div>
          </div>
        )}

        {/* ── Configuración (único acceso: hamburguesa) — hub de 4 filas que
             reutilizan exactamente el contenido/estado ya existente ── */}
        {tab === "settings" && settingsSection === null && (
          <div data-testid="settings-tab" className="max-w-lg">
            <h2 className="font-poppins text-lg font-bold text-slate-900 mb-4">{t("profileHub.rowSettings")}</h2>
            <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm overflow-hidden">
              <MenuRow label={t("mobileHeader.notifications")} onClick={() => setSettingsSection("notifications")} testid="settings-hub-row-notifications" />
              <MenuRow label={t("privacy.title")} onClick={() => setSettingsSection("privacy")} testid="settings-hub-row-privacy" />
              <MenuRow label={t("profileHub.rowCountryLanguage")} onClick={() => setSettingsSection("language")} testid="settings-hub-row-language" />
              <MenuRow label={t("profileHub.rowAccount")} onClick={() => setSettingsSection("account")} testid="settings-hub-row-account" />
            </div>
          </div>
        )}

        {tab === "settings" && settingsSection !== null && (
          <div data-testid="settings-section-view" className="max-w-lg space-y-6">
            <button type="button" onClick={() => setSettingsSection(null)} data-testid="settings-section-back-button"
              className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-700 transition-colors">
              <ChevronLeft className="h-4 w-4" /> {t("settingsHub.back")}
            </button>

            {settingsSection === "notifications" && (
              <div data-testid="settings-notifications-section">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">{t("mobileHeader.notifications")}</h3>
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-800">{t("settingsHub.soundLabel")}</p>
                      <p className="text-xs text-slate-500">{t("settingsHub.soundHint")}</p>
                    </div>
                    <Switch checked={soundEnabled} onCheckedChange={setSoundEnabled} data-testid="settings-sound-toggle" />
                  </div>
                </div>
              </div>
            )}

            {settingsSection === "privacy" && (
              <div data-testid="settings-privacy-tab">
                <PrivacySettingsPanel />
              </div>
            )}

            {settingsSection === "language" && (
              <div>
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">{t("profileHub.rowCountryLanguage")}</h3>
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 shadow-sm space-y-5">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">{t("countryLanguage.countryLabel")}</label>
                    <Select value={country} onValueChange={setCountry}>
                      <SelectTrigger data-testid="country-select-trigger" className="h-11 rounded-xl border-slate-200 text-sm font-semibold text-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {sortCountriesByPriority(countries.length ? countries : Object.keys(FLAGS).map((c) => ({ code: c })), t).map((c) => (
                          <SelectItem key={c.code} value={c.code} data-testid={`country-select-option-${c.code}`}>
                            <span className="flex items-center gap-2"><span>{FLAGS[c.code] || "🌐"}</span> {t(`geo.countryNames.${c.code}`)}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">{t("countryLanguage.languageLabel")}</label>
                    <Select value={language} onValueChange={changeLanguageGlobal}>
                      <SelectTrigger data-testid="language-select-trigger" className="h-11 rounded-xl border-slate-200 text-sm font-semibold text-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UI_LANGUAGES.map(({ code, flag, name }) => (
                          <SelectItem key={code} value={code} data-testid={`language-select-option-${code}`}>
                            <span className="flex items-center gap-2"><span>{flag}</span> {name}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {settingsSection === "account" && (
              <div>
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">{t("profileHub.rowContact")}</h3>
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 shadow-sm space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{t("profTab.nameLabel")}</label>
                    <input data-testid="contact-name-input" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full h-11 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{t("profTab.phoneFieldLabel")}</label>
                    <input data-testid="contact-phone-input" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      className="w-full h-11 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{t("profTab.locationFieldLabel")}</label>
                    <input data-testid="contact-location-input" value={contactForm.location} onChange={(e) => setContactForm({ ...contactForm, location: e.target.value })}
                      className="w-full h-11 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{t("contact.postalCodeLabel")}</label>
                    <input data-testid="contact-postal-input" value={contactForm.postal_code} onChange={(e) => setContactForm({ ...contactForm, postal_code: e.target.value })}
                      className="w-full h-11 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
                    <p className="text-xs text-slate-400 mt-1">{t("contact.postalCodePrivate")}</p>
                  </div>
                  <button onClick={saveContact} disabled={savingContact} data-testid="contact-save-button"
                    className="h-11 px-6 rounded-xl bg-[#028CF8] text-white text-sm font-semibold disabled:opacity-60">
                    {savingContact ? <Loader2 className="h-4 w-4 animate-spin inline" /> : t("profTab.save")}
                  </button>
                  {contactSaved && <p className="text-sm text-[#028CF8] font-semibold" data-testid="contact-saved-msg">{t("profTab.savedSuccess")}</p>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {viewingVersion && (
        <CVVersionViewModal
          version={viewingVersion}
          onClose={() => setViewingVersion(null)}
          onDownload={() => downloadVersionPdf(viewingVersion.version_id, viewingVersion.version_name)}
        />
      )}
      <CalendarPickerSheet
        open={!!calendarSheetApp}
        onClose={() => setCalendarSheetApp(null)}
        onSelect={handleCalendarSelect}
      />
      {offerDetail && (
        <OfferDetailSheet
          offer={offerDetail}
          onClose={() => { setOfferDetail(null); setSearchParams({ tab }, { replace: true }); }}
          onUpdated={(updated) => {
            setOfferDetail(updated);
            setApplications((prev) => prev.map((a) => a.application_id === updated.application_id ? { ...a, offer: updated } : a));
          }}
        />
      )}
    </div>
  );
}
